import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Rubric scoring: criteria scored on a rubric scale, optionally weighted, totalled two
 * ways.
 *
 * 1. **Linear** — points earned ÷ points possible. On a 1–4 rubric this turns
 *    "proficient" (3) into 75% and the lowest level into 25%, because rubric levels are
 *    ordered quality categories, not fractions of a test answered correctly.
 * 2. **Proficiency conversion** — the average rubric score mapped straight to a letter.
 *    The simple conversion Robert J. Marzano recommends for a 4-point proficiency scale
 *    (his own statement, 2018): A = 3.00–4.00, B = 2.50–2.99, C = 2.00–2.49,
 *    D = 1.00–1.99, F = below 1.00. A more detailed scale is in his *Formative
 *    Assessment & Standards-Based Grading* (Marzano Research, 2010), p. 106. It is one
 *    convention among several — schools publish their own conversion charts.
 */

export interface RubricCriterion {
  score: number;
  /** Relative weight; 1 when the criteria count equally. */
  weight: number;
}

export interface RubricResult {
  earned: number;
  possible: number;
  /** Earned ÷ possible × 100. */
  percent: number;
  /** Weighted mean score on the rubric scale. */
  average: number;
  totalWeight: number;
}

export function scoreRubric(
  criteria: readonly RubricCriterion[],
  max: number
): RubricResult | null {
  let earned = 0;
  let possible = 0;
  let totalWeight = 0;
  for (const criterion of criteria) {
    earned += criterion.weight * criterion.score;
    possible += criterion.weight * max;
    totalWeight += criterion.weight;
  }
  if (totalWeight <= 0 || possible <= 0) {
    return null;
  }
  return {
    earned: cleanFloat(earned),
    possible: cleanFloat(possible),
    percent: cleanFloat((earned * 100) / possible),
    average: cleanFloat(earned / totalWeight),
    totalWeight: cleanFloat(totalWeight),
  };
}

export interface ProficiencyBand {
  letter: string;
  /** Lowest average score, inclusive. */
  min: number;
}

/** Marzano's simple conversion for a 4-point proficiency scale. */
export const MARZANO_CONVERSION: readonly ProficiencyBand[] = [
  { letter: "A", min: 3 },
  { letter: "B", min: 2.5 },
  { letter: "C", min: 2 },
  { letter: "D", min: 1 },
  { letter: "F", min: Number.NEGATIVE_INFINITY },
];

/** The conversion is defined for 4-point scales (0–4 or 1–4) only. */
export function proficiencyLetter(average: number, max: number): string | null {
  if (max !== 4) {
    return null;
  }
  const value = cleanFloat(average);
  for (const band of MARZANO_CONVERSION) {
    if (value >= band.min) {
      return band.letter;
    }
  }
  return null;
}
